import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fs } from '../../firebase';
import { collection, serverTimestamp, doc, setDoc, updateDoc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useEffect, useRef } from 'react';
import { LocalizationContext } from "../../localization/LocalizationContext";
import { parseMultiLanguageDescription } from '../../utils/multiLanguageParser';
import API_ENDPOINTS, { API_PROXY_BASE } from '../../config/api';
import { sendToTelegram } from '../../utils/telegramHelper';
import saveAddressOnSuccess from '../../utils/saveAddressOnSuccess';
import { resolveSyrvePaymentType } from '../../utils/paymentTypes';
import { allowsMockPayment, isLocalDevelopmentHost, requiresBogPayment } from '../../utils/paymentMethod';
import { MINIMUM_ORDER_TOTAL_GEL, meetsMinimumOrderTotal } from '../../utils/orderLimits';

const SMS_CODE_COOLDOWN_SECONDS = 30;

const normalizeSmsCooldownPhone = (phoneNumber) => String(phoneNumber || '').replace(/\D/g, '');

const getSmsCooldownStorageKey = (phoneNumber) => `smsCodeCooldown:${normalizeSmsCooldownPhone(phoneNumber) || 'unknown'}`;

const fetchWithTimeout = async (url, options = {}, timeout = 30000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
        const response = await fetch(url, {
            ...options,
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error(`Request timeout after ${timeout/1000} seconds`);
        }
        throw error;
    }
};

const SFA_DELIVERY_CONFIG = {
    organizationId: 'cef12ab2-d9a5-4e3f-babb-11e6254849e8',
    terminalGroupId: '826d527c-bc55-495c-9ce2-43369609512e',
    name: 'SFA - Delivery'
};

const isFastDeliveryDomain = () => {
    const hostname = window.location.hostname;
    const isFastDelivery = hostname === 'fastdelivery.ge' ||
                          hostname === 'www.fastdelivery.ge' ||
                          hostname === 'artitalygroup.com' ||
                          hostname === 'www.artitalygroup.com' ||
                          hostname === 'localhost' ||
                          hostname.includes('europe-square');
    console.log('🌐 [DOMAIN-CHECK] Hostname:', hostname, '| Is SFA domain:', isFastDelivery);
    return isFastDelivery;
};

const getPaymentReturnBaseUrl = () => {
    const allowedHosts = [
        'fastdelivery.ge',
        'www.fastdelivery.ge',
        'artitalygroup.com',
        'www.artitalygroup.com',
        'europesquare.ge',
        'www.europesquare.ge'
    ];

    if (window.location.protocol === 'https:' && allowedHosts.includes(window.location.hostname)) {
        return window.location.origin;
    }

    return 'https://www.fastdelivery.ge';
};

const hasSfaDeliveryInCart = (ordersByVenue) => {
    const sfaKeys = ['SFA', 'SFA-Delivery', 'SFADelivery', 'sfa', 'sfa-delivery'];
    const venueKeys = Object.keys(ordersByVenue);
    const hasSfa = venueKeys.some(key =>
        sfaKeys.some(sfaKey => key.toLowerCase().includes(sfaKey.toLowerCase()))
    );
    console.log('🛒 [CART-CHECK] Venue keys:', venueKeys, '| Has SFA Delivery:', hasSfa);
    return hasSfa;
};

const awardLoyaltyPoints = async (params) => {
    const {
        token,
        organizationId,
        phoneNumber,
        points,
        orderType,
        firebaseOrderId,
        apiProxyBase
    } = params;

    try {
        console.log('🎁 [LOYALTY] Starting loyalty points award process');
        console.log('🎁 [LOYALTY] Points to award:', points, '| Order type:', orderType, '| Org ID:', organizationId);

        if (!phoneNumber || phoneNumber.trim() === '') {
            console.warn('⚠️ [LOYALTY] No phone number provided, skipping loyalty points');
            return { success: false, reason: 'no_phone' };
        }

        let formattedPhone = phoneNumber.trim();
        if (!formattedPhone.startsWith('+')) {
            formattedPhone = '+995' + formattedPhone.replace(/\D/g, '');
        }

        console.log('📞 [LOYALTY] Looking up customer by phone:', formattedPhone);
        const customerInfoRes = await fetch(`${apiProxyBase}/loyalty/syrve/customer/info`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                phone: formattedPhone,
                organizationId
            })
        });

        if (!customerInfoRes.ok) {
            const errorText = await customerInfoRes.text();
            console.warn('⚠️ [LOYALTY] Customer not found in Syrve:', errorText);
            return { success: false, reason: 'customer_not_found' };
        }

        const customerData = await customerInfoRes.json();
        const customerId = customerData.id;
        const walletBalances = customerData.walletBalances || [];

        console.log('👤 [LOYALTY] Customer found:', customerId);
        console.log('💰 [LOYALTY] Available wallets:', walletBalances.length);

        const bonusWallet = walletBalances.find(w => w.type === 1);

        if (!bonusWallet || !bonusWallet.id) {
            console.warn('⚠️ [LOYALTY] No bonus wallet found for customer');
            return { success: false, reason: 'no_wallet' };
        }

        const walletId = bonusWallet.id;
        console.log('🎯 [LOYALTY] Target wallet ID:', walletId, '| Wallet name:', bonusWallet.name);

        const topupRes = await fetch(`${apiProxyBase}/loyalty/syrve/customer/wallet/topup`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                customerId,
                walletId,
                sum: points,
                comment: `Order points: ${Math.floor(points)} (Order: ${firebaseOrderId || 'N/A'})`,
                organizationId
            })
        });

        if (!topupRes.ok) {
            const errorText = await topupRes.text();
            console.error('❌ [LOYALTY] Failed to award points:', errorText);
            return { success: false, reason: 'topup_failed', error: errorText };
        }

        const topupResult = await topupRes.json();
        console.log('✅ [LOYALTY] Points awarded successfully!');
        console.log('🎉 [LOYALTY] New balance:', bonusWallet.balance + points);

        return {
            success: true,
            customerId,
            walletId,
            pointsAwarded: points,
            newBalance: bonusWallet.balance + points,
            result: topupResult
        };

    } catch (error) {
        console.error('❌ [LOYALTY-ERROR] Error awarding loyalty points:', error);
        return { success: false, reason: 'exception', error: error.message };
    }
};

const FirebaseOrder = ({ orderData, selectedProducts, productQuantities, totalPrice, orderReceipt }) => {
    const navigate = useNavigate();
    const auth = getAuth();
    const user = auth.currentUser;
    const inputLength = 4;
    const [isCodeSent, setIsCodeSent] = React.useState(false);
    const [smsCode, setSmsCode] = React.useState('');
    const [sentCode, setSentCode] = React.useState('');
    const [isPhoneVerified, setIsPhoneVerified] = React.useState(false);
    const [showCodeModal, setShowCodeModal] = React.useState(false);
    const [showSuccessModal, setShowSuccessModal] = React.useState(false);
    const [error, setError] = React.useState('');
    const [bogToken, setBogToken] = useState(null);
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [codeResendTimer, setCodeResendTimer] = useState(0);
    const inputRefs = useRef([]);
    const codeTimerRef = useRef(null);

    useEffect(() => {
        if (showCodeModal) {
            setSmsCode('');
            inputRefs.current.forEach((input) => {
                if (input) input.value = '';
            });
            setTimeout(() => {
                inputRefs.current[0]?.focus(); // автофокус на первое поле
            }, 100);
        }
    }, [showCodeModal]);

    useEffect(() => {
        const storageKey = getSmsCooldownStorageKey(orderData.phoneNumber);
        const storedTime = localStorage.getItem(storageKey);

        if (!storedTime) {
            setCodeResendTimer(0);
            return;
        }

        const remaining = Math.ceil((Number(storedTime) - Date.now()) / 1000);
        if (remaining > 0) {
            setCodeResendTimer(remaining);
        } else {
            localStorage.removeItem(storageKey);
            setCodeResendTimer(0);
        }
    }, [orderData.phoneNumber]);

    useEffect(() => {
        if (codeResendTimer <= 0) return undefined;

        codeTimerRef.current = setInterval(() => {
            setCodeResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(codeTimerRef.current);
                    localStorage.removeItem(getSmsCooldownStorageKey(orderData.phoneNumber));
                    return 0;
                }

                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(codeTimerRef.current);
    }, [codeResendTimer, orderData.phoneNumber]);

    const startSmsCooldown = (seconds = SMS_CODE_COOLDOWN_SECONDS) => {
        const expiresAt = Date.now() + seconds * 1000;
        localStorage.setItem(getSmsCooldownStorageKey(orderData.phoneNumber), expiresAt.toString());
        setCodeResendTimer(seconds);
    };

    const closeModal = () => {
        setShowCodeModal(false);
        setSmsCode('');
    };
    function generateOrderId(length = 16) {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    }
    const handleOrderSubmit = async (paymentVerified = false) => {
        if (requiresBogPayment(orderData.paymentMethod) && !meetsMinimumOrderTotal(totalPrice)) {
            alert(translations?.deliveryPage?.minimumOrderMessage || `Minimum order is ${MINIMUM_ORDER_TOTAL_GEL} GEL`);
            return false;
        }

        if (!user) {
            alert(translations?.deliveryPage?.signInToOrder || 'Please sign in to place an order.');
            return false;
        }

        for (const [key, value] of Object.entries(orderData)) {
            if (key === 'changeFrom' || key === 'comment' || key === 'destination' || key === 'pickupLocation' || key === 'floor' || key === 'entrance' || key === 'apartment' || key === 'house') continue;
            if (typeof value === 'string' && value.trim() === '' || value === null || value === undefined) {
                alert(`${translations?.deliveryPage?.fillRequiredField || 'Please fill in the required field'}: ${key}`);
                return false;
            }
        }

        // Different validation based on order type
        if (orderData.orderType === 'delivery') {
            if (!orderData.destination?.address) {
                alert(translations?.deliveryPage?.enterDeliveryAddress || 'Please enter a delivery address.');
                return false;
            }
        } else if (orderData.orderType === 'self-pickup') {
            if (!orderData.selectedOrgId && !orderData.pickupFrom) {
                alert(translations?.deliveryPage?.selectRestaurantForPickup || 'Please select a restaurant for pickup.');
                return false;
            }
        } else if (orderData.orderType === 'dine-in') {
            if (!orderData.tableNum) {
                alert(translations?.deliveryPage?.selectTableNumber || 'Please select a table number.');
                return false;
            }
        }

        // Add payment verification check for online payments
        if (requiresBogPayment(orderData.paymentMethod) && !paymentVerified) {
            console.error('Cannot submit order: Online payment not verified');
            return false;
        }

        // Remove this line - use the ordersByVenue already defined at component level
        // const ordersByVenue = JSON.parse(localStorage.getItem("cartByCompany") || "{}");

        const products = [];

        Object.entries(ordersByVenue).forEach(([venue, items]) => {
            items.forEach(product => {
                const quantity = productQuantities[product.id] || 1;
                products.push({
                    productId: product.id,
                    quantity,
                    name: parseMultiLanguageDescription(product.title, locale) || translations?.deliveryPage?.unknownProduct || 'Unknown product',
                    price: product.price || 0,
                    company: venue, // добавляем откуда заказ
                    modifiers: product.modifiers || [], // если есть модификаторы
                });
            });
        });

        // Create orderReceipt as object for Firestore compatibility
        const orderReceiptObject = {};
        Object.entries(ordersByVenue).forEach(([venue, items]) => {
            orderReceiptObject[venue] = items.map(product => ({
                id: product.id,
                title: parseMultiLanguageDescription(product.title, locale) || translations?.deliveryPage?.unknownProduct || 'Unknown product',
                price: product.price || 0,
                quantity: productQuantities[product.id] || 1,
                modifiers: product.modifiers || []
            }));
        });

        const order = {
            ...orderData,
            orderReceipt: orderReceiptObject, // Now it's an object with venue keys
            orderType: orderData.orderType, // Use the correct order type from orderData
            totalPrice,
            products,
            userId: user.uid,
            recipientEmail: user.email, // Add user email for coin processing
            status: 'pending',
            createdAt: serverTimestamp(),
            paymentVerified: paymentVerified, // Track if payment was verified
            paymentStatus: requiresBogPayment(orderData.paymentMethod) ? (paymentVerified ? 'completed' : 'failed') : 'not_required'
        };

        try {
            const orderId = generateOrderId();
            localStorage.setItem('firebaseOrderId', orderId);
            const orderRef = doc(collection(fs, 'DeliveryOrder'), orderId);
            order.id = orderId;

            await setDoc(orderRef, order);

            return true;
        } catch (error) {
            console.error('Order submission error:', error);
            alert(translations?.deliveryPage?.orderPlacementError || 'Something went wrong while placing the order.');
            return false;
        }
    };


    const triggerStatusSync = () => {
        setTimeout(() => {
            fetch(API_ENDPOINTS.syncOrderStatuses, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            }).catch(() => {});
        }, 5000);
    };

    const sendTelegramNotification = async () => {
        try {
            const orderId = localStorage.getItem('firebaseOrderId');
            if (!orderId) return;
            const orderSnap = await getDoc(doc(fs, 'DeliveryOrder', orderId));
            if (orderSnap.exists()) {
                console.log('[TELEGRAM] Sending notification after successful order...');
                const telegramResult = await sendToTelegram(orderSnap.data());
                if (!telegramResult.success) {
                    console.error('[TELEGRAM] Failed:', telegramResult.error);
                } else {
                    console.log('[TELEGRAM] Notification sent successfully');
                }
            }
        } catch (err) {
            console.error('[TELEGRAM] Error sending notification:', err);
        }
    };

    const handleSendCode = async () => {
        console.log('🎯 [BUTTON-CLICK] Make Order button clicked');
        console.log('📍 [ENV] Location:', window.location.hostname);

        if (requiresBogPayment(orderData.paymentMethod) && !meetsMinimumOrderTotal(totalPrice)) {
            alert(translations?.deliveryPage?.minimumOrderMessage || `Minimum order is ${MINIMUM_ORDER_TOTAL_GEL} GEL`);
            return;
        }

        const isLocalhost = isLocalDevelopmentHost(window.location.hostname);
        const isMockPayment = allowsMockPayment(window.location.hostname, localStorage);

        if (isLocalhost) {
            console.log('📲 [LOCALHOST] Localhost detected - skipping OTP verification');
            setIsPhoneVerified(true);

            if (requiresBogPayment(orderData.paymentMethod) && !isMockPayment) {
                // Online: only redirect to BOG. PaymentReturn handles order creation, Syrve & Telegram after success.
                try {
                    await fetchBogAccessToken();
                } catch (error) {
                    alert(`${translations.paymentError || 'Payment error'}: ${error.message}`);
                    return;
                }
            } else {
                // Cash / mock: create order + Syrve + Telegram immediately.
                try {
                    console.log('[ORDER-FLOW] Step 1: Submitting Firebase order...');
                    const orderSubmitted = await handleOrderSubmit(requiresBogPayment(orderData.paymentMethod));
                    if (!orderSubmitted) return;
                    console.log('[ORDER-FLOW] Step 1: ✅ Firebase order submitted');

                    console.log('[ORDER-FLOW] Step 2: Creating Syrve orders...');
                    await handleMultiVenueOrder();
                    console.log('[ORDER-FLOW] Step 2: ✅ Syrve orders created');

                    await sendTelegramNotification();
                    localStorage.setItem("notification", JSON.stringify(true));
                    localStorage.removeItem('cartByCompany');
                    localStorage.removeItem('productQuantities');
                    localStorage.setItem('activeItem', '1');
                    navigate('/');
                } catch (error) {
                    console.error('[ORDER-FLOW] ❌ FAILED:', error);
                    alert(`${translations?.orderSubmissionError || 'Order failed. Please try again.'} ${error.message}`);
                    return;
                }
            }
            return;
        }

        // Production OTP flow
        if (codeResendTimer > 0) {
            alert(`Please wait ${codeResendTimer} seconds before requesting another code.`);
            return;
        }

        if (isSendingCode) return;

        try {
            setIsSendingCode(true);
            console.log('📲 Начата отправка SMS');
            const verificationCode = Math.floor(1000 + Math.random() * 9000).toString();
            setSentCode(verificationCode);

            // Remove all non-digit characters (smsoffice.ge requires no + sign)
            let cleanedNumber = orderData.phoneNumber.replace(/\D/g, '');
            if (!cleanedNumber.startsWith('995')) {
                cleanedNumber = '995' + cleanedNumber;
            }

            const response = await fetch(API_ENDPOINTS.sms, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    phoneNumbersArr: [cleanedNumber],
                    sender: 'es-sms',
                    smsText: `Ваш код: ${verificationCode}`,
                }),
            });

            const result = await response.json();
            console.log('Ответ от API:', result);

            if (response.status === 429) {
                const retryAfterSeconds = Number(result.retryAfterSeconds) || SMS_CODE_COOLDOWN_SECONDS;
                startSmsCooldown(retryAfterSeconds);
                alert(result.Message || `Please wait ${retryAfterSeconds} seconds before requesting another code.`);
                return;
            }

            if (!result.Success) {
                throw new Error(result.Message || 'Ошибка при отправке');
            }

            startSmsCooldown();
            setIsCodeSent(true);
            setShowCodeModal(true);
        } catch (error) {
            console.error('Ошибка отправки SMS:', error);
            alert(translations.errorSendingCode || 'Error sending code. Please check the number.');
        } finally {
            setIsSendingCode(false);
        }
    };

    const handleConfirmCode = async () => {
        if (smsCode === sentCode) {
            setError('');
            setIsPhoneVerified(true);
            setShowCodeModal(false);
            alert(translations.phoneConfirmed || "Phone confirmed!");

            const isMockPayment = allowsMockPayment(window.location.hostname, localStorage);

            if (requiresBogPayment(orderData.paymentMethod) && !isMockPayment) {
                // Online: only redirect to BOG. PaymentReturn handles order creation, Syrve & Telegram after success.
                try {
                    await fetchBogAccessToken();
                } catch (error) {
                    alert(`${translations.paymentError || 'Payment error'}: ${error.message}`);
                    return;
                }
            } else {
                try {
                    console.log('[ORDER-FLOW] Step 1: Submitting Firebase order...');
                    const orderSubmitted = await handleOrderSubmit(requiresBogPayment(orderData.paymentMethod));
                    if (!orderSubmitted) return;
                    console.log('[ORDER-FLOW] Step 1: ✅ Firebase order submitted');

                    console.log('[ORDER-FLOW] Step 2: Creating Syrve orders...');
                    await handleMultiVenueOrder();
                    console.log('[ORDER-FLOW] Step 2: ✅ Syrve orders created');

                    await sendTelegramNotification();
                    localStorage.setItem("notification", JSON.stringify(true));
                    localStorage.removeItem('cartByCompany');
                    localStorage.removeItem('productQuantities');
                    localStorage.setItem('activeItem', '1');
                    navigate('/');
                } catch (error) {
                    console.error('[ORDER-FLOW] ❌ FAILED:', error);
                    alert(`${translations?.orderSubmissionError || 'Order failed. Please try again.'} ${error.message}`);
                    return;
                }
            }
        } else {
            alert(translations.incorrectCode || "Incorrect code");
        }
    };
    const checkPaymentStatus = async (orderBogId, bogToken) => {
        try {
            const response = await fetch(API_ENDPOINTS.proxy.bogReceipt(orderBogId), {
                headers: {
                    'Authorization': `Bearer ${bogToken}`,
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                const text = await response.text();
                console.error("Ответ не OK, текст:", text);
                throw new Error(`Ошибка сети: ${response.status}`);
            }

            const data = await response.json();
            const statusKey = data.order_status?.key;

            console.log("Текущий статус оплаты (key):", statusKey);

            if (statusKey === 'completed') {
                return 'completed';
            } else {
                return 'pending';
            }
        } catch (error) {
            console.error('Ошибка при проверке статуса заказа:', error);
            throw error;
        }
    };

    let isCheckingPayment = false;

    const waitForPaymentSuccess = (orderBogId, bogToken, timeoutMs = 5 * 60 * 1000, intervalMs = 3000) => {
        return new Promise((resolve, reject) => {
            if (isCheckingPayment) {
                reject(new Error('Проверка уже запущена'));
                return;
            }

            isCheckingPayment = true;
            const startTime = Date.now();

            const intervalId = setInterval(async () => {
                try {
                    const elapsed = Date.now() - startTime;

                    if (elapsed > timeoutMs) {
                        clearInterval(intervalId);
                        isCheckingPayment = false;
                        reject(new Error('Время ожидания оплаты истекло'));
                        return;
                    }

                    const status = await checkPaymentStatus(orderBogId, bogToken);
                    if (status === 'completed') {
                        clearInterval(intervalId);
                        isCheckingPayment = false;
                        resolve(true);
                    }
                } catch (err) {
                    clearInterval(intervalId);
                    isCheckingPayment = false;
                    reject(err);
                }
            }, intervalMs);
        });
    };



    const getVenueTotalsAsObject = (ordersByVenue) => {
        return Object.entries(ordersByVenue).reduce((acc, [venue, items]) => {
            const total = items.reduce((sum, item) => sum + item.price, 0);
            acc[venue] = Number(total.toFixed(2));
            return acc;
        }, {});
    };


    useEffect(() => {
        const totals = getVenueTotalsAsObject(ordersByVenue);
        console.log(totals);
    })


    const VENUE_NAME_MAP = {
        "Aromiitaliani": "Aromi Italiani",
        "CopaCabana": "Copacabana",
        "Nadimi": "Nadimi",
        "PoshNoshs": "Poshnosh",
        "Symposium": "Symposium 2"
        // Cave Club не участвует в заказах — не включаем
    };






    const { translations, locale } = useContext(LocalizationContext);



    const [isOrdering, setIsOrdering] = useState(false);
    const [organizationId, setOrganizationId] = useState(null);
    const [terminalGroupId, setTerminalGroupId] = useState(null);
    const [token, setToken] = useState(null); // Если токен нужен для авторизации
    const [orderTypeId, setOrderTypeId] = useState(null);
    const [choosedPaymentType, setChoosedPaymentType] = useState(null); // Добавляем состояние для выбранного типа оплаты
    const ordersByVenue = JSON.parse(localStorage.getItem("cartByCompany") || "{}");

    console.log('ordersByVenue', ordersByVenue)
    console.log('Orderdata', orderData)
    const handleMultiVenueOrder = async () => {
        console.log('🚀 [ORDER-START] Starting handleMultiVenueOrder');
        console.log('📋 [ORDER-DATA] Order type:', orderData.orderType);
        console.log('📋 [ORDER-DATA] Full order data:', JSON.stringify(orderData, null, 2));
        console.log('🛒 [CART-DATA] Orders by venue:', JSON.stringify(ordersByVenue, null, 2));

        try {
            const useSfaDeliveryForToken = isFastDeliveryDomain() || hasSfaDeliveryInCart(ordersByVenue);
            const apiLogin = useSfaDeliveryForToken
                ? "ace87d94922a4f399ca68496897ba644"
                : "df27763ebfa044aa9ab31a11ff1ad042";

            console.log('🔑 [AUTH] Fetching Syrve access token from:', `${API_PROXY_BASE}/access_token`);
            console.log('🔑 [AUTH] Using', useSfaDeliveryForToken ? 'SFA/AGGREGATOR' : 'INDIVIDUAL', 'API token');
            console.log('🔑 [AUTH] API token (first 20 chars):', apiLogin.substring(0, 20));

            const tokenRes = await fetchWithTimeout(`${API_PROXY_BASE}/access_token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ apiLogin }),
            }, 10000);
            const tokenData = await tokenRes.json();
            const token = tokenData.token;
            console.log('✅ [AUTH] Token received:', token ? 'Yes (length: ' + token.length + ')' : 'No');

            if (!token || token.length === 0) {
                console.error('❌ [AUTH] Failed to obtain Syrve access token');
                throw new Error('Failed to authenticate with Syrve POS. Please try again.');
            }

            const orgRes = await fetchWithTimeout(`${API_PROXY_BASE}/organizations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    organizationIds: [],
                    returnAdditionsInfo: true,
                    includeDisabled: false,
                }),
            }, 10000);

            const orgData = await orgRes.json();

            if (!orgRes.ok) {
                console.error('❌ [ORG-FETCH] Failed to fetch organizations:', orgData);
                throw new Error('Failed to fetch restaurant data from Syrve POS');
            }

            const allOrganizations = orgData.organizations;

            if (!allOrganizations || allOrganizations.length === 0) {
                console.error('❌ [ORG-FETCH] No organizations returned from Syrve');
                throw new Error('No restaurant organizations found in POS system');
            }

            const useSfaDelivery = isFastDeliveryDomain() || hasSfaDeliveryInCart(ordersByVenue);
            console.log('🚚 [SFA-CHECK] Use SFA Delivery:', useSfaDelivery, '(domain:', isFastDeliveryDomain(), ', cart:', hasSfaDeliveryInCart(ordersByVenue), ')');

            if (useSfaDelivery) {
                console.log('🚚 [SFA-DELIVERY] Orders will be sent to SFA-Delivery terminal');
                console.log('🔑 [SFA-DELIVERY] API token (first 20 chars):', token.substring(0, 20));

                const allProducts = [];
                for (const [venueName, products] of Object.entries(ordersByVenue)) {
                    if (products && products.length > 0) {
                        products.forEach(product => {
                            allProducts.push({ ...product, sourceVenue: venueName });
                        });
                    }
                }

                if (allProducts.length === 0) {
                    console.warn('⚠️ [SFA-DELIVERY] No products to order');
                    return;
                }

                const organizationId = SFA_DELIVERY_CONFIG.organizationId;
                const terminalGroupId = SFA_DELIVERY_CONFIG.terminalGroupId;

                console.log('✅ [SFA-DELIVERY] Using organization:', organizationId);
                console.log('✅ [SFA-DELIVERY] Using terminal group:', terminalGroupId);

                const orderTypesUrl = (orderData.orderType === 'delivery' || orderData.orderType === 'self-pickup')
                    ? `${API_PROXY_BASE}/deliveries/order_types`
                    : `${API_PROXY_BASE}/order-types`;

                const orderTypeRes = await fetchWithTimeout(orderTypesUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ organizationIds: [organizationId] }),
                }, 10000);
                const orderTypesData = await orderTypeRes.json();

                let orderTypeId;
                if (orderData.orderType === "delivery") {
                    const deliveryType = orderTypesData?.orderTypes?.[0]?.items?.find(
                        (item) =>
                            item.name === "Доставка курьером" &&
                            item.orderServiceType === "DeliveryByCourier" &&
                            item.isDeleted === false
                    );
                    orderTypeId = deliveryType?.id;

                    if (!orderTypeId) {
                        console.error('❌ [ORDER-TYPE] Delivery order type not found');
                        console.error('Available order types:', orderTypesData?.orderTypes?.[0]?.items);
                        throw new Error('Delivery order type not configured in POS');
                    }
                } else if (orderData.orderType === "self-pickup") {
                    const pickupType = orderTypesData?.orderTypes?.[0]?.items?.find(
                        (item) =>
                            (item.name.toLowerCase().includes("самовывоз") ||
                                item.name.toLowerCase().includes("pickup") ||
                                item.name.toLowerCase().includes("take") ||
                                item.name === "Self-service") &&
                            item.orderServiceType === "DeliveryByClient" &&
                            item.isDeleted === false
                    );
                    orderTypeId = pickupType?.id;

                    if (!orderTypeId) {
                        console.error('❌ [ORDER-TYPE] Self-pickup order type not found');
                        console.error('Available order types:', orderTypesData?.orderTypes?.[0]?.items);
                        throw new Error('Self-pickup order type not configured in POS');
                    }
                } else {
                    const dineIn = orderTypesData?.orderTypes?.[0]?.items?.find(
                        (item) =>
                            item.name === "Dine-In Order" &&
                            item.orderServiceType === "Common" &&
                            item.isDeleted === false
                    );
                    orderTypeId = dineIn?.id;

                    if (!orderTypeId) {
                        console.error('❌ [ORDER-TYPE] Dine-in order type not found');
                        console.error('Available order types:', orderTypesData?.orderTypes?.[0]?.items);
                        throw new Error('Dine-in order type not configured in POS');
                    }
                }

                const paymentRes = await fetchWithTimeout(`${API_PROXY_BASE}/payment-types`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token, organizationIds: [organizationId] }),
                }, 10000);
                const paymentData = await paymentRes.json();
                const paymentTypes = paymentData?.paymentTypes || [];

                const {
                    paymentTypeId,
                    paymentTypeKind,
                    paymentTypeCode,
                    paymentTypeName
                } = resolveSyrvePaymentType(paymentTypes, orderData.paymentMethod, 'SFA-Delivery');

                console.log('✅ [PAYMENT-TYPE] Using:', paymentTypeName, paymentTypeCode, paymentTypeId);

                const orderItems = allProducts.map(product => ({
                    type: "Product",
                    amount: productQuantities[product.id] || 1,
                    productId: product.id,
                    modifiers: product.modifiers || [],
                    comment: product.specialInstructions || '',
                }));

                const aggregatorTotal = allProducts.reduce((sum, product) => {
                    return sum + (product.price * (productQuantities[product.id] || 1));
                }, 0);

                const payments = [{
                    paymentTypeKind,
                    sum: aggregatorTotal,
                    paymentTypeId,
                    isProcessedExternally: false,
                    isFiscalizedExternally: false,
                    isPrepay: false
                }];

                const now = new Date();
                const offsetMinutes = 30;
                const georgiaOffset = 4 * 60;
                const localTime = new Date(now.getTime() + offsetMinutes * 60 * 1000 + georgiaOffset * 60 * 1000);
                const completeBefore = localTime.toISOString().replace('T', ' ').replace('Z', '').split('.')[0] + '.000';

                let formattedPhone = orderData.phoneNumber.trim();
                if (!formattedPhone.startsWith('+')) {
                    formattedPhone = '+995' + formattedPhone.replace(/\D/g, '');
                }

                const baseOrder = {
                    id: crypto.randomUUID(),
                    phone: formattedPhone,
                    externalNumber: localStorage.getItem("firebaseOrderId"),
                    completeBefore,
                    customer: { name: orderData.recipientName, type: "one-time" },
                    items: orderItems,
                    payments,
                    comment: orderData.comment || '',
                    ...(orderData.tableNum ? { tableIds: [orderData.tableNum] } : {})
                };

                if (orderData.orderType === 'delivery') {
                    baseOrder.orderServiceType = "DeliveryByCourier";
                    if (!orderData.destination?.lat || !orderData.destination?.lng) {
                        throw new Error("Для доставки необходимо указать lat и lng");
                    }
                    baseOrder.deliveryPoint = {
                        coordinates: { latitude: orderData.destination.lat, longitude: orderData.destination.lng },
                        address: {
                            street: { name: orderData.destination.address || 'Customer address' },
                            house: orderData.house?.trim() || orderData.apartment?.trim() || '-',
                            flat: orderData.entrance?.trim() || '-',
                            type: "string"
                        },
                        externalCartographyId: orderData.destination?.externalCartographyId?.trim() || "-",
                        comment: (orderData.comment || '') + ` Phone: ${orderData.phoneNumber}` + (orderData.deliveryTime ? ` Time: ${orderData.deliveryTime}` : '')
                    };
                } else if (orderData.orderType === 'self-pickup') {
                    baseOrder.orderServiceType = "DeliveryByClient";
                } else {
                    baseOrder.orderTypeId = orderTypeId;
                    baseOrder.terminalGroupId = terminalGroupId;
                    baseOrder.guestCount = orderData.personsCount;
                }

                const orderPayload = {
                    organizationId,
                    terminalGroupId,
                    order: baseOrder,
                    createOrderSettings: { servicePrint: false, transportToFrontTimeout: 10, checkStopList: false }
                };

                const createOrderUrl = (orderData.orderType === 'delivery' || orderData.orderType === 'self-pickup')
                    ? `${API_PROXY_BASE}/deliveries/create`
                    : `${API_PROXY_BASE}/create-order`;

                console.log('📦 [SFA-DELIVERY] Creating aggregator order:', JSON.stringify(orderPayload, null, 2));

                const createOrderRes = await fetchWithTimeout(createOrderUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token, orderPayload }),
                }, 30000);

                if (!createOrderRes.ok) {
                    const errorText = await createOrderRes.text();
                    console.error('❌ [SFA-DELIVERY] HTTP error:', createOrderRes.status, errorText);

                    let errorData;
                    try {
                        errorData = JSON.parse(errorText);
                    } catch (e) {
                        throw new Error(`Syrve POS HTTP ${createOrderRes.status}: ${errorText}`);
                    }

                    throw new Error(errorData.error || `Syrve POS error: ${createOrderRes.status}`);
                }

                const createOrderResult = await createOrderRes.json();
                console.log('✅ [SFA-DELIVERY] Aggregator order result:', createOrderResult);

                if (createOrderResult.error) {
                    console.error('❌ [SFA-DELIVERY] Error in response:', createOrderResult);
                    throw new Error(createOrderResult.error || 'Syrve POS returned an error');
                }

                if (!createOrderResult.orderInfo?.id && !createOrderResult.correlationId) {
                    console.error('❌ [SFA-DELIVERY] Invalid response:', createOrderResult);
                    throw new Error('Syrve POS returned incomplete response (no order ID)');
                }

                if (createOrderResult.orderInfo?.id || createOrderResult.correlationId) {
                    const firebaseOrderId = localStorage.getItem('firebaseOrderId');
                    if (firebaseOrderId) {
                        const orderRef = doc(fs, 'DeliveryOrder', firebaseOrderId);
                        const updateData = {
                            'syrveOrganizationIds.SFA-Delivery': organizationId,
                            orderSource: 'fastdelivery.ge',
                            isAggregatorOrder: true
                        };
                        if (createOrderResult.orderInfo?.id) {
                            updateData['syrveOrderIds.SFA-Delivery'] = createOrderResult.orderInfo.id;
                        }
                        if (createOrderResult.correlationId) {
                            updateData['syrveCorrelationIds.SFA-Delivery'] = createOrderResult.correlationId;
                        }
                        await updateDoc(orderRef, updateData);
                    }
                }

                const pointsToAward = Math.floor(aggregatorTotal);
                if (pointsToAward > 0) {
                    console.log('🎁 [LOYALTY] Attempting to award points for aggregator order');
                    const loyaltyResult = await awardLoyaltyPoints({
                        token,
                        organizationId,
                        phoneNumber: orderData.phoneNumber,
                        points: pointsToAward,
                        orderType: orderData.orderType,
                        firebaseOrderId: localStorage.getItem('firebaseOrderId'),
                        apiProxyBase: API_PROXY_BASE
                    });

                    if (loyaltyResult.success) {
                        console.log('🎉 [LOYALTY] Aggregator: Successfully awarded', pointsToAward, 'points');
                        const firebaseOrderId = localStorage.getItem('firebaseOrderId');
                        if (firebaseOrderId) {
                            await updateDoc(doc(fs, 'DeliveryOrder', firebaseOrderId), {
                                'loyaltyPointsAwarded.SFA-Delivery': pointsToAward,
                                'loyaltyAwardedAt.SFA-Delivery': serverTimestamp(),
                                'loyaltyCustomerId.SFA-Delivery': loyaltyResult.customerId,
                                'loyaltyWalletId.SFA-Delivery': loyaltyResult.walletId
                            });
                        }
                    }
                }

                console.log('✅ [SFA-DELIVERY] Aggregator order completed successfully');
                localStorage.removeItem('cartByCompany');
                localStorage.removeItem('productQuantities');
                triggerStatusSync();
                setShowSuccessModal(true);
                return;
            }

            console.log('🏪 [INDIVIDUAL] Processing as individual restaurant orders');
            for (const [venueName, products] of Object.entries(ordersByVenue)) {
                console.log(`🔄 [VENUE-LOOP] Processing venue: ${venueName}`);

                if (!products || products.length === 0) {
                    console.warn(`⚠️ [SKIP] Venue "${venueName}" skipped - no products`);
                    continue;
                }

                console.log('🔍 [ORG-LOOKUP] Looking for organization with name:', VENUE_NAME_MAP[venueName]);
                console.log('📋 [ORG-LOOKUP] Available organizations:', allOrganizations.map(o => o.name));

                const org = allOrganizations.find(o => o.name === VENUE_NAME_MAP[venueName]);

                if (!org) {
                    console.error('❌ [ORG-ERROR] Organization not found for venue:', venueName);
                    console.error('Looking for name:', VENUE_NAME_MAP[venueName]);
                    console.error('Available organizations:', allOrganizations);
                    throw new Error(`Organization not found for venue: ${venueName} (mapped name: ${VENUE_NAME_MAP[venueName]})`);
                }

                const organizationId = org.id;
                console.log('✅ [ORG-FOUND] Organization ID for', venueName, ':', organizationId);

                // terminalGroupId
                const terminalRes = await fetchWithTimeout(`${API_PROXY_BASE}/terminalgroups`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ organizationIds: [organizationId] }),
                }, 10000);
                const terminalData = await terminalRes.json();
                const terminalGroupId = terminalData?.terminalGroups?.[0]?.items?.[0]?.id;

                // orderTypeId - both delivery and self-pickup use deliveries endpoint
                const orderTypesUrl = (orderData.orderType === 'delivery' || orderData.orderType === 'self-pickup')
                    ? `${API_PROXY_BASE}/deliveries/order_types`
                    : `${API_PROXY_BASE}/order-types`;

                const orderTypeRes = await fetchWithTimeout(orderTypesUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ organizationIds: [organizationId] }),
                }, 10000);
                const orderTypesData = await orderTypeRes.json();

                // Log available order types for debugging
                console.log(`Available order types for ${venueName}:`,
                    orderTypesData?.orderTypes?.[0]?.items?.map(item => ({
                        name: item.name,
                        orderServiceType: item.orderServiceType,
                        isDeleted: item.isDeleted,
                        id: item.id
                    }))
                );

                let orderTypeId;

                if (orderData.orderType === "delivery") {
                    // For delivery, use courier delivery
                    const deliveryType = orderTypesData?.orderTypes?.[0]?.items?.find(
                        (item) =>
                            item.name === "Доставка курьером" &&
                            item.orderServiceType === "DeliveryByCourier" &&
                            item.isDeleted === false
                    );
                    orderTypeId = deliveryType?.id;
                    console.log(`Order type for ${orderData.orderType}:`, deliveryType?.name, orderTypeId);

                    if (!orderTypeId) {
                        console.error(`❌ [ORDER-TYPE] Delivery order type not found for ${venueName}`);
                        console.error('Available order types:', orderTypesData?.orderTypes?.[0]?.items);
                        throw new Error(`Delivery order type not configured in POS for ${venueName}`);
                    }
                } else if (orderData.orderType === "self-pickup") {
                    // For self-pickup, look for DeliveryByClient order type
                    const pickupType = orderTypesData?.orderTypes?.[0]?.items?.find(
                        (item) =>
                            (item.name.toLowerCase().includes("самовывоз") ||
                                item.name.toLowerCase().includes("pickup") ||
                                item.name.toLowerCase().includes("take") ||
                                item.name === "Self-service") &&
                            item.orderServiceType === "DeliveryByClient" &&
                            item.isDeleted === false
                    );
                    orderTypeId = pickupType?.id;
                    console.log(`Order type for ${orderData.orderType}:`, pickupType?.name, orderTypeId);

                    if (!orderTypeId) {
                        console.error(`❌ [ORDER-TYPE] Self-pickup order type not found for ${venueName}`);
                        console.error('Available order types:', orderTypesData?.orderTypes?.[0]?.items);
                        throw new Error(`Self-pickup order type not configured in POS for ${venueName}`);
                    }
                } else {
                    // fallback, например "Dine-In Order" или другой
                    const dineIn = orderTypesData?.orderTypes?.[0]?.items?.find(
                        (item) =>
                            item.name === "Dine-In Order" &&
                            item.orderServiceType === "Common" &&
                            item.isDeleted === false
                    );
                    orderTypeId = dineIn?.id;

                    if (!orderTypeId) {
                        console.error(`❌ [ORDER-TYPE] Dine-in order type not found for ${venueName}`);
                        console.error('Available order types:', orderTypesData?.orderTypes?.[0]?.items);
                        throw new Error(`Dine-in order type not configured in POS for ${venueName}`);
                    }
                }

                // paymentTypeId (динамически)
                const paymentRes = await fetchWithTimeout(`${API_PROXY_BASE}/payment-types`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        token,
                        organizationIds: [organizationId],
                    }),
                }, 10000);
                const paymentData = await paymentRes.json();
                const paymentTypes = paymentData?.paymentTypes || [];

                const {
                    paymentTypeId,
                    paymentTypeKind,
                    paymentTypeCode,
                    paymentTypeName
                } = resolveSyrvePaymentType(paymentTypes, orderData.paymentMethod, venueName);

                console.log(`✅ [PAYMENT-TYPE] Using for ${venueName}:`, paymentTypeName, paymentTypeCode, paymentTypeId);




                // позиции заказа
                const orderItems = products.map(product => ({
                    type: "Product",
                    amount: productQuantities[product.id] || 1,
                    productId: product.id,
                    modifiers: product.modifiers || [],
                    comment: product.specialInstructions || '',
                }));

                const venueTotal = products.reduce((sum, product) => {
                    return sum + (product.price * (productQuantities[product.id] || 1));
                }, 0);

                const payments = [
                    {
                        paymentTypeKind,
                        sum: venueTotal,
                        paymentTypeId,
                        isProcessedExternally: false,
                        isFiscalizedExternally: false,
                        isPrepay: false
                    }
                ];
                const now = new Date();
                const offsetMinutes = 30; // на сколько минут вперёд от текущего времени
                const georgiaOffset = 4 * 60; // смещение Грузии от UTC в минутах

                const localTime = new Date(now.getTime() + offsetMinutes * 60 * 1000 + georgiaOffset * 60 * 1000);

                const completeBefore = localTime
                    .toISOString()
                    .replace('T', ' ')
                    .replace('Z', '')
                    .split('.')[0] + '.000'; // оставляем только миллисекунды как .000

                console.log(completeBefore);

                // Ensure phone number starts with + for Syrve API
                let formattedPhone = orderData.phoneNumber.trim();
                if (!formattedPhone.startsWith('+')) {
                    // Assume Georgian number if no country code
                    formattedPhone = '+995' + formattedPhone.replace(/\D/g, '');
                }

                const baseOrder = {
                    id: crypto.randomUUID(),
                    phone: formattedPhone,
                    externalNumber: localStorage.getItem("firebaseOrderId"),
                    completeBefore,
                    customer: {
                        name: orderData.recipientName,
                        type: "one-time",
                    },
                    items: orderItems,
                    payments,
                    comment: orderData.comment || '',
                    ...(orderData.tableNum ? { tableIds: [orderData.tableNum] } : {})
                };

                // Add orderTypeId or orderServiceType based on the endpoint
                if (orderData.orderType === 'delivery') {
                    baseOrder.orderServiceType = "DeliveryByCourier";
                } else if (orderData.orderType === 'self-pickup') {
                    baseOrder.orderServiceType = "DeliveryByClient";
                } else {
                    // For dine-in and other orders, use orderTypeId
                    baseOrder.orderTypeId = orderTypeId;
                    baseOrder.terminalGroupId = terminalGroupId;
                    baseOrder.guestCount = orderData.personsCount;
                }

                // Если тип доставки — добавим deliveryPoint
                if (orderData.orderType === 'delivery') {
                    // For delivery orders, we need full address details
                    if (!orderData.destination?.lat || !orderData.destination?.lng) {
                        console.error("Ошибка: обязательные поля доставки не заполнены");
                        throw new Error("Для доставки необходимо указать lat и lng");
                    }
                    baseOrder.deliveryPoint = {
                        coordinates: {
                            latitude: orderData.destination.lat,
                            longitude: orderData.destination.lng
                        },
                        address: {
                            street: { name: orderData.destination.address || 'Customer address' },
                            house: orderData.house?.trim() || orderData.apartment?.trim() || '-',
                            flat: orderData.entrance?.trim() || '-',
                            type: "string"
                        },
                        externalCartographyId: orderData.destination?.externalCartographyId?.trim() || "-",
                        comment: (orderData.comment || '') + ` Phone: ${orderData.phoneNumber}` + (orderData.deliveryTime ? ` Time: ${orderData.deliveryTime}` : '')
                    };
                } else if (orderData.orderType === 'self-pickup') {
                    // For self-pickup orders, we'll try different approaches based on a flag
                    // Start without deliveryPoint, if that fails, we can try with minimal structure
                    const USE_DELIVERY_POINT_FOR_PICKUP = false; // Toggle this if the order fails without deliveryPoint

                    if (USE_DELIVERY_POINT_FOR_PICKUP) {
                        console.log('Self-pickup order: Using minimal deliveryPoint');
                        baseOrder.deliveryPoint = {
                            coordinates: {
                                latitude: 0,
                                longitude: 0
                            },
                            address: {
                                street: {
                                    // Try without hardcoded ID first
                                    name: 'Pickup Location'
                                },
                                house: '-',
                                type: "legacy"
                            },
                            comment: "Self pickup from restaurant"
                        };
                    } else {
                        console.log('Self-pickup order: Testing without deliveryPoint');
                    }
                }

                const orderPayload = {
                    organizationId,
                    terminalGroupId,
                    order: baseOrder,

                    createOrderSettings: {
                        servicePrint: false,
                        transportToFrontTimeout: 10,
                        checkStopList: false
                    }
                };




                const createOrderUrl = (orderData.orderType === 'delivery' || orderData.orderType === 'self-pickup')
                    ? `${API_PROXY_BASE}/deliveries/create`
                    : `${API_PROXY_BASE}/create-order`;

                console.log(`Creating order for ${venueName}:`, {
                    orderType: orderData.orderType,
                    endpoint: createOrderUrl,
                    orderServiceType: baseOrder.orderServiceType,
                    orderTypeId: baseOrder.orderTypeId,
                    hasDeliveryPoint: !!baseOrder.deliveryPoint
                });
                console.log('🔑 [SYRVE-API] API Token', token);
                console.log('Full order payload:', JSON.stringify(orderPayload, null, 2));

                const createOrderRes = await fetchWithTimeout(createOrderUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ token, orderPayload }),
                }, 30000);

                if (!createOrderRes.ok) {
                    const errorText = await createOrderRes.text();
                    console.error(`❌ [${venueName}] HTTP error:`, createOrderRes.status, errorText);

                    let errorData;
                    try {
                        errorData = JSON.parse(errorText);
                    } catch (e) {
                        throw new Error(`${venueName}: Syrve POS HTTP ${createOrderRes.status}: ${errorText}`);
                    }

                    console.error(`❌ [${venueName}] Detailed error:`, {
                        error: errorData.error,
                        errorDescription: errorData.errorDescription,
                        violations: errorData.violations,
                        validationErrors: errorData.validationErrors
                    });

                    throw new Error(`${venueName}: ${errorData.error || `Syrve POS error ${createOrderRes.status}`}`);
                }

                const createOrderResult = await createOrderRes.json();
                console.log(`✅ [${venueName}] Order result:`, createOrderResult);

                if (createOrderResult.error) {
                    console.error(`❌ [${venueName}] Error in response:`, createOrderResult);
                    console.error(`Detailed error for ${venueName}:`, {
                        error: createOrderResult.error,
                        errorDescription: createOrderResult.errorDescription,
                        violations: createOrderResult.violations,
                        validationErrors: createOrderResult.validationErrors
                    });
                    throw new Error(`${venueName}: ${createOrderResult.error}`);
                }

                if (!createOrderResult.orderInfo?.id && !createOrderResult.correlationId) {
                    console.error(`❌ [${venueName}] Invalid response:`, createOrderResult);
                    throw new Error(`${venueName}: Syrve POS returned incomplete response`);
                }

                // Store Syrve order ID and correlationId for status tracking
                if (createOrderResult.orderInfo?.id || createOrderResult.correlationId) {
                    const firebaseOrderId = localStorage.getItem('firebaseOrderId');
                    if (firebaseOrderId) {
                        // Store Syrve order ID and correlationId in Firebase for status syncing
                        const orderRef = doc(fs, 'DeliveryOrder', firebaseOrderId);
                        const updateData = {
                            [`syrveOrganizationIds.${venueName}`]: organizationId
                        };

                        if (createOrderResult.orderInfo?.id) {
                            updateData[`syrveOrderIds.${venueName}`] = createOrderResult.orderInfo.id;
                        }

                        if (createOrderResult.correlationId) {
                            updateData[`syrveCorrelationIds.${venueName}`] = createOrderResult.correlationId;
                        }

                        await updateDoc(orderRef, updateData);
                    }
                }

                const venueTotalForPoints = products.reduce((sum, product) => {
                    const quantity = productQuantities[product.id] || 1;
                    return sum + (product.price * quantity);
                }, 0);
                const pointsToAward = Math.floor(venueTotalForPoints);

                if (pointsToAward > 0) {
                    console.log(`🎁 [LOYALTY] Attempting to award points for ${venueName}`);
                    const loyaltyResult = await awardLoyaltyPoints({
                        token,
                        organizationId,
                        phoneNumber: orderData.phoneNumber,
                        points: pointsToAward,
                        orderType: orderData.orderType,
                        firebaseOrderId: localStorage.getItem('firebaseOrderId'),
                        apiProxyBase: API_PROXY_BASE
                    });

                    if (loyaltyResult.success) {
                        console.log(`🎉 [LOYALTY] ${venueName}: Successfully awarded`, pointsToAward, 'points');
                        const firebaseOrderId = localStorage.getItem('firebaseOrderId');
                        if (firebaseOrderId) {
                            await updateDoc(doc(fs, 'DeliveryOrder', firebaseOrderId), {
                                [`loyaltyPointsAwarded.${venueName}`]: pointsToAward,
                                [`loyaltyAwardedAt.${venueName}`]: serverTimestamp(),
                                [`loyaltyCustomerId.${venueName}`]: loyaltyResult.customerId,
                                [`loyaltyWalletId.${venueName}`]: loyaltyResult.walletId
                            });
                        }
                    } else {
                        console.warn(`⚠️ [LOYALTY] ${venueName}: Failed to award points:`, loyaltyResult.reason);
                    }
                }
            }

            console.log('✅ [ORDER-COMPLETE] All orders processed successfully');

            const firebaseOrderId = localStorage.getItem('firebaseOrderId');
            if (firebaseOrderId) {
                try {
                    await updateDoc(doc(fs, 'DeliveryOrder', firebaseOrderId), {
                        posSubmissionStatus: 'success',
                        posSubmittedAt: serverTimestamp()
                    });
                    console.log('✅ [FIREBASE] Tracked successful POS submission');
                } catch (updateError) {
                    console.error('❌ [FIREBASE] Failed to update success status:', updateError);
                }
            }

            await saveAddressOnSuccess();
            localStorage.removeItem('cartByCompany');
            localStorage.removeItem('productQuantities');
            triggerStatusSync();
            setShowSuccessModal(true);
        } catch (error) {
            console.error("❌ [ORDER-ERROR] Error during order submission:", error);
            console.error("Error details:", {
                message: error.message,
                stack: error.stack
            });

            const firebaseOrderId = localStorage.getItem('firebaseOrderId');
            if (firebaseOrderId) {
                try {
                    await updateDoc(doc(fs, 'DeliveryOrder', firebaseOrderId), {
                        posSubmissionStatus: 'failed',
                        posSubmissionError: error.message,
                        posFailedAt: serverTimestamp()
                    });
                    console.log('✅ [FIREBASE] Tracked failed POS submission');
                } catch (updateError) {
                    console.error('❌ [FIREBASE] Failed to update error status:', updateError);
                }
            }

            alert(translations.orderSubmissionError || "Error occurred while submitting orders: " + error.message);
        }
    };


    const [isFetchingBogToken, setIsFetchingBogToken] = useState(false);

    // Reset isFetchingBogToken on component mount or when returning from payment
    useEffect(() => {
        setIsFetchingBogToken(false);
    }, []);

    // fetchBogAccessToken - ждем, пока пользователь оплатит или закроет окно, потом возвращаем результат
    // В fetchBogAccessToken - используем orderBogId для проверки статуса оплаты
    const fetchBogAccessToken = async () => {
        if (isFetchingBogToken) return Promise.reject(new Error('Уже идёт запрос токена'));
        setIsFetchingBogToken(true);

        try {
            const response = await fetch(API_ENDPOINTS.proxy.bogAuth, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
            });

            const data = await response.json();

            if (!response.ok || !data.access_token) {
                throw new Error('BOG авторизация не удалась');
            }

            setBogToken(data.access_token);

            // This will redirect to payment page and not return
            await handleBogCreateOrder(data.access_token);

            // Code below won't be reached due to redirect
            return true;

        } catch (error) {
            console.error("Ошибка при обработке платежа BOG:", error.message);
            setIsFetchingBogToken(false);
            throw error;
        }
    };


    // handleBogCreateOrder - сохраняем данные заказа и делаем редирект на оплату
    // В handleBogCreateOrder
    const handleBogCreateOrder = async (token) => {
        const useToken = token || bogToken;

        if (!useToken) {
            alert(translations?.paymentError || 'Payment error');
            throw new Error('BOG токен не получен');
        }

        try {
            const basket = buildBogBasket();

            const orderId = crypto.randomUUID(); // локальный ID для внутреннего учета

            // Store order data in localStorage before redirect
            const pendingOrderData = {
                orderData,
                selectedProducts,
                productQuantities,
                totalPrice,
                orderReceipt,
                orderId,
                timestamp: Date.now()
            };
            localStorage.setItem('pendingBogPayment', JSON.stringify(pendingOrderData));

            // BOG requires HTTPS URLs. On production domains, return to the same origin
            // so PaymentReturn can read the pending payment data from localStorage.
            const baseUrl = getPaymentReturnBaseUrl();

            const orderPayload = {
                amount: totalPrice,
                currency: "GEL",
                callbackUrl: `${baseUrl}/payment-return`,
                orderId,
                basket,
                redirect_urls: {
                    success: `${baseUrl}/payment-return?status=success&orderId=${orderId}`,
                    fail: `${baseUrl}/payment-return?status=failed&orderId=${orderId}`
                }
            };

            console.log("BOG request payload:", JSON.stringify(orderPayload, null, 2));
            console.log("BOG endpoint:", API_ENDPOINTS.proxy.bogCreateOrder);

            const response = await fetch(API_ENDPOINTS.proxy.bogCreateOrder, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${useToken}`,
                },
                body: JSON.stringify(orderPayload),
            });

            const data = await response.json();
            console.log("BOG response status:", response.status);
            console.log("BOG response data:", JSON.stringify(data, null, 2));

            if (!response.ok) {
                console.error("BOG API error - Full response:", {
                    status: response.status,
                    statusText: response.statusText,
                    data: data
                });
                throw new Error(`BOG API error: ${response.status} - ${data?.error?.message || data?.message || data?.errors?.[0]?.message || JSON.stringify(data)}`);
            }

            const redirectUrl = data._links?.redirect?.href;
            const orderBogId = data.orderId || data.id; // <-- Получаем orderBogId из ответа

            if (redirectUrl && orderBogId) {
                // Store BOG order ID for verification after return
                localStorage.setItem('pendingBogOrderId', orderBogId);
                localStorage.setItem('bogToken', useToken);

                // Redirect to payment page instead of popup
                window.location.href = redirectUrl;

                // Return won't be reached due to redirect, but kept for consistency
                return { orderId, orderBogId, redirectUrl };
            } else {
                console.error("Missing redirectUrl or orderBogId in response:", { redirectUrl, orderBogId, data });
                throw new Error(`Missing payment data: redirectUrl=${!!redirectUrl}, orderBogId=${!!orderBogId}`);
            }
        } catch (error) {
            console.error("Ошибка при создании BOG заказа:", error);
            alert(`${translations?.paymentError || 'Payment error'}: ${error.message}`);
            throw error;
        }
    };

    const buildBogBasket = () => {
        const basket = [];

        for (const venueProducts of Object.values(ordersByVenue)) {
            if (!Array.isArray(venueProducts)) continue;

            for (const product of venueProducts) {
                const quantity = productQuantities[product.id] || 1;

                if (!product.id || typeof product.id !== "string") {
                    console.warn("Пропущен товар с недопустимым product_id:", product);
                    continue; // Пропускаем неверный product_id
                }

                basket.push({
                    product_id: product.id, // строго snake_case как требует BOG
                    quantity: quantity,
                    unit_price: product.price || 0
                });
            }
        }

        return basket;
    };
    const isBelowMinimumOnlineOrder = requiresBogPayment(orderData.paymentMethod) && !meetsMinimumOrderTotal(totalPrice);
    const isDisabled = !user || isSendingCode || codeResendTimer > 0 || isBelowMinimumOnlineOrder;
    const buttonText = isDisabled
        ? (
            isBelowMinimumOnlineOrder
                ? (translations.deliveryPage?.minimumOrderMessage || `Minimum order is ${MINIMUM_ORDER_TOTAL_GEL} GEL`)
                : !user
                ? (translations.deliveryPage?.signInToOrder || 'Sign in to make an order')
                : codeResendTimer > 0
                    ? `Request code again in ${codeResendTimer}s`
                    : (translations.deliveryPage?.sendingCode || 'Sending code...')
        )
        : `${translations.deliveryPage?.makeOrder || 'Make order'} (${totalPrice}ლ)`;

    return (
        <div>
            <button
                className="MakeOrderBtn"
                onClick={handleSendCode}
                disabled={isDisabled}
                style={{
                    opacity: isDisabled ? 0.6 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                }}
            >
                {buttonText}
            </button>
            {showCodeModal && (
                <div className="delivery-order-modal-overlay">
                    <div className="delivery-order-modal">
                        <h3 className="delivery-order-modal-title">{translations.enterSMSCode || 'Enter SMS code'}</h3>
                        <div className="delivery-order-code-inputs">
                            {[...Array(inputLength)].map((_, i) => (
                                <input
                                    key={i}
                                    type="text"
                                    maxLength="1"
                                    className="delivery-order-digit-input"
                                    ref={(el) => inputRefs.current[i] = el}
                                    value={smsCode[i] || ''}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '');
                                        if (!val) return;
                                        const updated = smsCode.split('');
                                        updated[i] = val[0];
                                        setSmsCode(updated.join(''));

                                        // Перейти к следующему полю
                                        if (i < inputLength - 1) {
                                            inputRefs.current[i + 1]?.focus();
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Backspace') {
                                            if (smsCode[i]) {
                                                const updated = smsCode.split('');
                                                updated[i] = '';
                                                setSmsCode(updated.join(''));
                                            } else if (i > 0) {
                                                inputRefs.current[i - 1]?.focus();
                                            }
                                        }
                                    }}
                                />
                            ))}
                        </div>
                        <button className="delivery-order-confirm-btn" onClick={handleConfirmCode}>
                            {translations.confirmCode || 'Confirm'}
                        </button>
                    </div>
                </div>
            )}

            {showSuccessModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        background: '#fff', borderRadius: '16px', padding: '32px 24px',
                        textAlign: 'center', maxWidth: '320px', width: '100%'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px', color: '#111' }}>
                            შეკვეთა წარმატებით გაიგზავნა
                        </h3>
                        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
                            შეგიძლიათ ნახოთ შეკვეთებში
                        </p>
                        <button
                            onClick={() => { setShowSuccessModal(false); localStorage.setItem('activeItem', '1'); navigate('/'); }}
                            style={{
                                background: '#ef4444', color: '#fff', border: 'none',
                                borderRadius: '10px', padding: '12px 24px',
                                fontSize: '15px', fontWeight: '600', cursor: 'pointer', width: '100%'
                            }}
                        >
                            დახურვა
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default FirebaseOrder;
